import React from 'react'
import { Button, Drawer, Well } from '@pushly/aqe/lib/components'
import { EditOutlined } from '@ant-design/icons'
import { Container } from 'typescript-ioc/es5'
import { AccountDto } from '../../dtos/account.dto'
import { AccountService, AppService } from '../../services/index'
import { getClassNames } from '../../_utils/classnames'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT, SUPPORTED_BILLING_CURRENCIES } from '../../constants'
import { SETTINGS_WELL_FORM_LAYOUT } from '../../theme/constants'
import { simpleNotification } from '../../_utils/utils'
import { ManageBillingCustomerRequest } from '@pushly/models/lib/structs/billing'
import { AppState } from '../../stores/app'
import { Stripe, StripeElements } from '@stripe/stripe-js'
import { CardElement, ElementsConsumer } from '@stripe/react-stripe-js'
import { InjectedStripeForm } from '../stripe-form/injected-stripe-form'
import { Form, Input, Skeleton, Typography, notification } from 'antd'

interface IAccountBillingEditor {
    account: AccountDto
    visible: boolean
    onClose: (e: any) => void
    onAccountUpdated: (account: Partial<AccountDto>) => void
    stripe: Stripe | null
    elements: StripeElements | null
}

interface IABEState {}

const AccountBillingEditorComponent: React.FC<IAccountBillingEditor> = (props) => {
    const [form] = Form.useForm()
    const appState = Container.get(AppState)
    const appService = Container.get(AppService)
    const accountService = Container.get(AccountService)

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields()

            const { stripe, elements } = props
            if (!stripe || !elements) {
                throw new Error('Unable to process card information at this time.')
            }

            const card = elements.getElement(CardElement)
            if (!card) {
                throw new Error('No card information found. Please fill out the form and try again.')
            }

            const { token, error } = await stripe.createToken(card)
            if (error !== undefined) {
                throw new Error(error.message)
            }

            if (!token || !token.card) {
                throw {
                    card: {
                        errors: [{ message: 'Please specify a valid credit card.' }],
                    },
                }
            }

            const changes = ManageBillingCustomerRequest.from({
                stripe_customer_email: values.contactEmail,
                stripe_customer_token: token.id,
                stripe_customer_last4: token.card.last4,
            })

            const domain = appState.currentDomain!
            const { ok, data: billingData } = await accountService.manageBilling(domain.accountId, changes)

            if (ok) {
                props.onAccountUpdated({ billingData })
            }
        } catch (err) {
            simpleNotification('error', err)
        }
    }

    return (
        <Drawer
            getContainer={appService.getAppContainer}
            className={getClassNames('adf-billing-editor')}
            title="Update Billing"
            placement="right"
            closable={true}
            disableSubmit={!props.stripe || !props.elements}
            onSubmit={handleSubmit}
            onClose={props.onClose}
            open={props.visible}
            width={800}
        >
            <Form
                form={form}
                {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                requiredMark={false}
                size="small"
                labelAlign="left"
                styles={{
                    content: { marginBottom: 12 },
                }}
            >
                <Well title="Billing Information" mode="ghost" hideFooter={true} flush>
                    <Form.Item
                        label="Contact Email"
                        name="contactEmail"
                        initialValue={props.account.billingData.stripe_customer_email}
                        rules={[{ required: true, message: 'Contact email is required' }]}
                    >
                        <Input size="small" autoComplete="off" />
                    </Form.Item>

                    <Form.Item label="Billing Credit Card" className={getClassNames('adf-billing-editor-cc')}>
                        <CardElement />
                    </Form.Item>
                </Well>
            </Form>
        </Drawer>
    )
}

export const AccountBillingEditor = AccountBillingEditorComponent

interface IAccountBillingWell {
    account: AccountDto
    onAccountUpdated: (account: Partial<AccountDto>) => void
    loading?: boolean
    title?: string
}

interface IABWState {
    showDrawer: boolean
}

export class AccountBillingWell extends React.Component<IAccountBillingWell, IABWState> {
    public state: IABWState = {
        showDrawer: false,
    }

    protected appService: AppService

    public constructor(props: IAccountBillingWell) {
        super(props)

        this.appService = Container.get(AppService)
    }

    public render() {
        return (
            <Well
                className="nested"
                title="Billing"
                actions={
                    <Button size="small" onClick={this.handleDrawerOpen} disabled={this.props.loading}>
                        <EditOutlined />
                        <span>Edit</span>
                    </Button>
                }
                hideFooter={true}
            >
                <Skeleton loading={this.props.loading} active={true} title={false}>
                    {!this.props.loading && (
                        <>
                            <Form
                                {...SETTINGS_WELL_FORM_LAYOUT}
                                colon={false}
                                requiredMark={false}
                                labelAlign="left"
                                size="small"
                            >
                                {this.renderBillingInformation()}
                            </Form>

                            <InjectedStripeForm>
                                <ElementsConsumer>
                                    {({ stripe, elements }) => (
                                        <AccountBillingEditor
                                            visible={this.state.showDrawer}
                                            account={this.props.account}
                                            onClose={this.handleDrawerClose}
                                            onAccountUpdated={(account) => {
                                                this.props.onAccountUpdated(account)
                                                return this.handleDrawerClose()
                                            }}
                                            stripe={stripe}
                                            elements={elements}
                                        />
                                    )}
                                </ElementsConsumer>
                            </InjectedStripeForm>
                        </>
                    )}
                </Skeleton>
            </Well>
        )
    }

    protected renderBillingInformation() {
        return (
            <Well title="Billing Configuration" mode="ghost" hideFooter={true} padding="none">
                <Form.Item label="Contact Email">
                    <Typography.Text>{this.currentValues.stripe_customer_email || '(Not Set)'}</Typography.Text>
                </Form.Item>

                {this.currentValues.stripe_customer_id && (
                    <Form.Item label="Current Card">
                        <Typography.Text>**** **** **** {this.currentValues.stripe_customer_last4}</Typography.Text>
                    </Form.Item>
                )}
            </Well>
        )
    }

    protected get propsValue(): any {
        return this.props.account.billingData || {}
    }

    protected get currentValues() {
        const propsValue = this.propsValue

        const values: any = {
            type: (propsValue.type || 'INVOICE').toUpperCase(),
            currency: (propsValue.currency || 'USD').toUpperCase(),
        }

        if (values.type === 'CREDIT') {
            values.rate_type = (propsValue.rate_type || 'FLAT_RATE').toUpperCase()
            values.rate = propsValue.rate
        }

        if (propsValue.stripe_customer_id) {
            values.stripe_customer_id = propsValue.stripe_customer_id
            values.stripe_customer_email = propsValue.stripe_customer_email
            values.stripe_customer_last4 = propsValue.stripe_customer_last4
        }

        return values
    }

    protected get currentTypeIsCredit(): boolean {
        return this.currentValues.type === 'CREDIT'
    }

    protected get currentRateTypeIsFlat(): boolean {
        return this.currentValues.rate_type === 'FLAT_RATE'
    }

    protected get currentCurrencyDisplayName(): string {
        return SUPPORTED_BILLING_CURRENCIES.find((cur) => cur.value === this.currentValues.currency)!.label
    }

    protected get currentCurrencySymbol(): string {
        return SUPPORTED_BILLING_CURRENCIES.find((cur) => cur.value === this.currentValues.currency)!.symbol
    }

    protected handleDrawerOpen = async () => {
        return this.setState({ showDrawer: true })
    }

    protected handleDrawerClose = async () => {
        return this.setState({ showDrawer: false })
    }

    protected handleCopied = (field: string) => {
        notification.destroy()
        simpleNotification('success', `Your ${field} has been copied!`)
    }
}
